import axios from "axios";
import { OpenAPI } from "@propertelligent/client-api";

// TODO(client-api-regen): Replace these thin axios wrappers with the generated
// `@propertelligent/client-api` counts methods after the backend PR merges and CI
// republishes the SDK. Expected generated names per the shared API contract include:
// getApiUnitOrganizationlistCounts, getApiUnitPropertylistCounts,
// getApiTenantOrganizationlistCounts, getApiTenantPropertylistCounts,
// getApiServiceRequest{Organization,Property,Unit,Tenant,Contractor}listCounts,
// getApiContractorOrganizationlistCounts, getApiPaymentDueAccountOrganizationlistCounts,
// getApiPayeeAccountOrganizationlistCounts, getApiOrganizationUserOrganizationlistCounts,
// and getApiFeedbackOrganizationlistCounts.

export interface ListCountsResponse<T = unknown[]> {
  success?: T;
  recordCount?: number;
  aggregateValues?: Record<string, number>;
  errors?: Record<string, string[]>;
}

type ListCountsRequestParams = {
  pageSize?: number;
  pageNumber?: number;
  skipValue?: number;
  filterEntityTypeId?: number;
  filterEntityIds?: Array<number>;
  fullyQualifiedFilters?: string | null;
  fullyQualifiedSorts?: string;
  dateFilterDateFilterTypeId?: number;
  dateFilterClientDate?: string;
  dateFilterStartDate?: string;
  dateFilterEndDate?: string;
  dateFilterDateField?: string;
  dateFilterFormatted?: Record<string, any>;
  formatted?: Record<string, any>;
};

type ScopedCountsRequest = (
  scopeId: number,
  pageSize?: number,
  pageNumber?: number,
  skipValue?: number,
  filterEntityTypeId?: number,
  filterEntityIds?: Array<number>,
  fullyQualifiedFilters?: string | null,
  fullyQualifiedSorts?: string,
  dateFilterDateFilterTypeId?: number,
  dateFilterClientDate?: string,
  dateFilterStartDate?: string,
  dateFilterEndDate?: string,
  dateFilterDateField?: string,
  dateFilterFormatted?: Record<string, any>,
  formatted?: Record<string, any>,
) => Promise<ListCountsResponse>;

const baseUrl = (): string => (typeof OpenAPI.BASE === "string" ? OpenAPI.BASE : "");

const authHeaders = (): Record<string, string> => {
  const token = typeof OpenAPI.TOKEN === "string" ? OpenAPI.TOKEN : undefined;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const toQuery = (params: ListCountsRequestParams) => ({
  PageSize: params.pageSize,
  PageNumber: params.pageNumber,
  SkipValue: params.skipValue,
  FilterEntityTypeId: params.filterEntityTypeId,
  FilterEntityIds: params.filterEntityIds,
  FullyQualifiedFilters: params.fullyQualifiedFilters ?? undefined,
  FullyQualifiedSorts: params.fullyQualifiedSorts,
  "DateFilter.DateFilterTypeId": params.dateFilterDateFilterTypeId,
  "DateFilter.ClientDate": params.dateFilterClientDate,
  "DateFilter.StartDate": params.dateFilterStartDate,
  "DateFilter.EndDate": params.dateFilterEndDate,
  "DateFilter.DateField": params.dateFilterDateField,
  "DateFilter.Formatted": params.dateFilterFormatted,
  Formatted: params.formatted,
});

const getCounts = async (
  path: string,
  params: ListCountsRequestParams = {},
): Promise<ListCountsResponse> => {
  const res = await axios.get(`${baseUrl()}${path}`, {
    headers: authHeaders(),
    params: toQuery(params),
  });
  return res.data;
};

const createScopedCountsGetter = (pathBuilder: (scopeId: number) => string): ScopedCountsRequest => {
  return (
    scopeId,
    pageSize,
    pageNumber,
    skipValue,
    filterEntityTypeId,
    filterEntityIds,
    fullyQualifiedFilters,
    fullyQualifiedSorts,
    dateFilterDateFilterTypeId,
    dateFilterClientDate,
    dateFilterStartDate,
    dateFilterEndDate,
    dateFilterDateField,
    dateFilterFormatted,
    formatted,
  ) =>
    getCounts(pathBuilder(scopeId), {
      pageSize,
      pageNumber,
      skipValue,
      filterEntityTypeId,
      filterEntityIds,
      fullyQualifiedFilters,
      fullyQualifiedSorts,
      dateFilterDateFilterTypeId,
      dateFilterClientDate,
      dateFilterStartDate,
      dateFilterEndDate,
      dateFilterDateField,
      dateFilterFormatted,
      formatted,
    });
};

export const getApiUnitOrganizationlistCounts = createScopedCountsGetter(
  (organizationId) => `/api/Unit/${organizationId}/organizationlist/counts`,
);

export const getApiUnitPropertylistCounts = createScopedCountsGetter(
  (propertyId) => `/api/Unit/${propertyId}/propertylist/counts`,
);

export const getApiTenantOrganizationlistCounts = createScopedCountsGetter(
  (organizationId) => `/api/Tenant/${organizationId}/organizationlist/counts`,
);

export const getApiTenantPropertylistCounts = createScopedCountsGetter(
  (propertyId) => `/api/Tenant/${propertyId}/propertylist/counts`,
);

export const getApiServiceRequestOrganizationlistCounts = createScopedCountsGetter(
  (organizationId) => `/api/ServiceRequest/${organizationId}/organizationlist/counts`,
);

export const getApiServiceRequestPropertylistCounts = createScopedCountsGetter(
  (propertyId) => `/api/ServiceRequest/${propertyId}/propertylist/counts`,
);

export const getApiServiceRequestUnitlistCounts = createScopedCountsGetter(
  (unitId) => `/api/ServiceRequest/${unitId}/unitlist/counts`,
);

export const getApiServiceRequestTenantlistCounts = createScopedCountsGetter(
  (tenantId) => `/api/ServiceRequest/${tenantId}/tenantlist/counts`,
);

export const getApiServiceRequestContractorlistCounts = createScopedCountsGetter(
  (contractorId) => `/api/ServiceRequest/${contractorId}/contractorlist/counts`,
);

export const getApiContractorOrganizationlistCounts = createScopedCountsGetter(
  (organizationId) => `/api/Contractor/${organizationId}/organizationlist/counts`,
);

export const getApiPaymentDueAccountOrganizationlistCounts = createScopedCountsGetter(
  (organizationId) => `/api/PaymentDueAccount/${organizationId}/organizationlist/counts`,
);

export const getApiPayeeAccountOrganizationlistCounts = createScopedCountsGetter(
  (organizationId) => `/api/PayeeAccount/${organizationId}/organizationlist/counts`,
);

export const getApiOrganizationUserOrganizationlistCounts = createScopedCountsGetter(
  (organizationId) => `/api/OrganizationUser/${organizationId}/organizationlist/counts`,
);

export const getApiFeedbackOrganizationlistCounts = createScopedCountsGetter(
  (organizationId) => `/api/Feedback/${organizationId}/organizationlist/counts`,
);

// getApiFeedbackUserlistCounts (Feedback/{userId}/userlist/counts) and
// getApiFeedbackPlatformAllCounts (Feedback/platform/all/counts) were dropped: no current
// screen calls either (FeedbackComponent.tsx only ever uses the organization-scoped route
// above, and the platform Support Requests table — SupportRequests.tsx — has no filter pills
// to source counts for). Re-add them, backed by an actual caller, if/when Support Requests
// grows pills or a user-scoped Feedback list ships.
